import React, { useState, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
  IconButton,
  FormControl,
  FormHelperText,
  FormGroup,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import FacebookIcon from "@mui/icons-material/Facebook";
import jsonData from "./loginconfig.json";
import Chip from "@mui/material/Chip";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import InputAdornment from "@mui/material/InputAdornment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

const iconMap = {
  google: GoogleIcon,
  apple: AppleIcon,
  facebook: FacebookIcon,
};

const LoginPage = ({ setShowAppBar }) => {
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [activeLogin, setActiveLogin] = useState(true);
  const navigate = useNavigate();

  useLayoutEffect(() => {
    setShowAppBar(false);
    return () => {
      setShowAppBar(true);
    };
  }, [setShowAppBar]);

  const handleclick = (button) => {
    if (button.text === "Create Account") {
      navigate("/createaccountoptions");
    }
    if (button.text === "Back") {
      navigate("/");
    }
  };
  const getGridWidth = (width) => Math.round(12 * (parseFloat(width) / 100));

  const validateField = (name, value, isRequired) => {
    if (isRequired && !value) {
      return "This field is required";
    }
    return "";
  };

  const handleInputChange = (e, isRequired) => {
    const { name, value } = e.target;
    if (name === "email") {
      setActiveLogin(!value ? true : false);
    }
    const error = validateField(name, value, isRequired);
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = () => {
    const newErrors = {};
    jsonData.layout[2].columns[0].fields.forEach((field) => {
      const error = validateField(
        field.name,
        formValues[field.name],
        field.required
      );
      if (error) newErrors[field.name] = error;
    });
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      console.log("Form submitted:", formValues);
    }
  };

  const rednerPage = (jsonData) => {
    return (
      <Grid container sx={{ height: "100vh", padding: 2 }}>
        {/* Row 1: Buttons at Top */}
        <Grid
          container
          item
          justifyContent="space-between"
          alignItems="center"
          sx={{ position: "absolute", top: 0, left: 0, right: 0, padding: 2 }}
        >
          {jsonData.layout[0].columns.map((button, index) => (
            <Button
              key={index}
              onClick={() => handleclick(button)}
              startIcon={button.text === "Back" && <ArrowBackIosNewIcon />}
            >
              {button.text}
            </Button>
          ))}
        </Grid>

        {/* Centered Content */}
        <Grid container item justifyContent="center" alignItems="center">
          <Grid container justifyContent="center" alignItems="center">
            {/* Row 2: Icon with Header */}
            <Box sx={{ padding: 3 }}>
              <Typography
                fontWeight="bold"
                color="primary"
                sx={{
                  textAlign: "center", // Center the header
                  padding: "16px", // Add spacing around the text
                  letterSpacing: "0.1em", // Add spacing between letters
                  textTransform: "uppercase", // Optional: Convert text to uppercase
                }}
              >
                {jsonData.layout[1].columns[0].header}
              </Typography>
            </Box>
          </Grid>

          {/* Row 3: Form and Social Buttons */}
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{
              padding: 2, // Add padding to the container
              maxWidth: 850,
            }}
          >
            {/* Form */}
            <Grid
              item
              xs={12}
              sm={getGridWidth(jsonData.layout[2].columns[0].width)}
            >
              <Box
                component="form"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Loop through fields from layout and create TextField for each */}
                {jsonData.layout[2].columns[0].fields.map((field, index) => (
                  <FormControl
                    key={index}
                    fullWidth
                    error={!!errors[field.name]}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <TextField
                      name={field.name}
                      label={field.label}
                      type={
                        field.type === "password"
                          ? showPassword
                            ? "text"
                            : "password"
                          : field.type
                      }
                      variant="outlined"
                      fullWidth
                      value={formValues[field.name] || ""}
                      onChange={(e) => handleInputChange(e, field.required)}
                      InputProps={{
                        endAdornment:
                          field.type === "password" ? (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword((prev) => !prev)}
                                edge="end"
                              >
                                {showPassword ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                      }}
                    />
                    {errors[field.name] && (
                      <FormHelperText>{errors[field.name]}</FormHelperText>
                    )}
                  </FormControl>
                ))}
                <Button
                  variant="contained"
                  color="primary"
                  disabled={activeLogin}
                  fullWidth
                  onClick={handleSubmit}
                  sx={{
                    textTransform: "none",
                    padding: "12px", // Adds padding inside the button
                  }}
                >
                  {jsonData.layout[2].columns[0].actions[0].text}
                </Button>
              </Box>
            </Grid>

            {/* Divider */}
            <Grid
              item
              xs={12}
              sm={getGridWidth(jsonData.layout[2].columns[1].width)}
            >
              <Divider orientation="vertical">
                <Chip label="OR" size="small" />
              </Divider>
            </Grid>

            {/* Social Buttons */}
            <Grid
              item
              xs={12}
              sm={getGridWidth(jsonData.layout[2].columns[2].width)}
            >
              <Grid container spacing={2} justifyContent="center">
                {jsonData.layout[2].columns[2].buttons.map((button, index) => {
                  const Icon = iconMap[button.icon];
                  return (
                    <Grid item xs={12} sm={12} md={12} key={index}>
                      <Button
                        variant="outlined"
                        startIcon={Icon ? <Icon /> : null}
                        component={Link}
                        to="/Signin"
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start", // Ensures the icon appears at the start
                          gap: 4, // Adds space between the icon and the text
                          textTransform: "none", // Optional: Remove automatic uppercase from button text
                          padding: "10px 20px",
                          width: {
                            xs: "100%", // Button takes full width on extra-small screens
                            md: "80%", // 60% width on medium screens
                          },
                        }}
                      >
                        {button.label}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Row 4: Footer Text */}
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ padding: 2 }}
        >
          {jsonData.layout[3].columns.map((footerItem, index) => (
            <Typography
              key={index}
              variant={footerItem.type === "text" ? "body1" : "body2"}
              color={footerItem.type === "text" ? "primary" : "textSecondary"}
              sx={{
                cursor: footerItem.action ? "pointer" : "default",
                fontWeight: footerItem.type === "text" ? "bold" : "normal",
                textTransform: footerItem.type === "text" ? "uppercase" : "",
                margin: 1,
                display: "block",
              }}
              onClick={() =>
                footerItem.action && console.log(footerItem.action)
              }
            >
              {footerItem.content}
            </Typography>
          ))}
        </Grid>
      </Grid>
    );
  };

  return rednerPage(jsonData);
};

export default LoginPage;
